import { Controller } from "@hotwired/stimulus";
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { post, destroy } from '@rails/request.js'

import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';

import FileChecksum from "../../../../../javascript/utils/file-checksum";
import './styles.css'

export default class extends Controller {
  static targets = [
    "assetFileContainer",
    "assetFileTemplate",
    "dashboardTrigger",
    "form",
  ]

  static values = {
    allowedFileTypes: Array,
    directUploadUrl: String,
    destroyBlobUrl: String,
  }

  uppy = undefined

  state =  {
    files: [],
  }

  setState(newState) {
    this.state = { ...this.state, ...newState }

    if (this.render) {
      this.render()
    }
  }

  resetState() {
    this.setState({ files: [] })
  }

  connect() {
    if (this.hasFormTarget) {
      document.addEventListener("comment:created", (event) => {
        this.resetState()
      })
    }

    this.uppy = new Uppy({
      allowMultipleUploads: true,
      autoProceed: false,
      restrictions: {
        allowedFileTypes: this.allowedFileTypesValue,
      },
    })
      .use(AwsS3, {
        endpoint: '/uploads',
        // Generate Presigned Upload Url
        getUploadParameters: async (file) => {
          const { meta } = file
          
          const presignData = await this.presignUpload(file)

          this.uppy.setFileMeta(file.id, { ...meta, ...presignData })

          return presignData
        },
      })
      .use(Dashboard, {
        showProgressDetails: true,
        target: document.querySelector('body'),
        theme: document.documentElement.classList.contains('light') ? 'light' : 'dark',
        trigger: this.dashboardTriggerTarget,
      })
      .on('upload-success', this.uploadSuccess.bind(this))
  }

  disconnect(){
    if (this.uppy){
      this.uppy = undefined
    }
  }

  async presignUpload(file) {
    const { data, name, size, type } = file

    // Get a checksum for the file
    const checksum = await FileChecksum.create(data)

    // Get a presigned upload url from Rails
    const presignBody = {
      blob: {
        filename: name,
        content_type: type,
        byte_size: size,
        checksum,
      },
    }
    
    const presignResponse = await post(this.directUploadUrlValue, {
      body: presignBody,
      responseKind: 'json',
    })

    // Example response from Rails
    //   {
    //     "id": 494,
    //     "key": "d255y81unzlhdx6l15j4trk9tobb",
    //     "filename": "Building a Dashboard with View Components (1).jpg",
    //     "content_type": "image/jpeg",
    //     "metadata": {},
    //     "service_name": "local",
    //     "byte_size": 585070,
    //     "checksum": "LLMSCD/kowVoq7siAkS0nQ==",
    //     "created_at": "2024-09-10T03:26:57.287Z",
    //     "attachable_sgid": "eyJfcmFpbHMiOnsiZGF0YSI6ImdpZDovL2NsaXBmbG93L0FjdGl2ZVN0b3JhZ2U6OkJsb2IvNDk0P2V4cGlyZXNfaW4iLCJwdXIiOiJhdHRhY2hhYmxlIn19--2bad84e203925046fd67af4394d98ff37d39ad45",
    //     "signed_id": "eyJfcmFpbHMiOnsiZGF0YSI6NDk0LCJwdXIiOiJibG9iX2lkIn19--a39743126898e32db6542318c210656e2babc183",
    //     "direct_upload": {
    //         "url": "http://localhost:3000/rails/active_storage/disk/eyJfcmFpbHMiOnsiZGF0YSI6eyJrZXkiOiJkMjU1eTgxdW56bGhkeDZsMTVqNHRyazl0b2JiIiwiY29udGVudF90eXBlIjoiaW1hZ2UvanBlZyIsImNvbnRlbnRfbGVuZ3RoIjo1ODUwNzAsImNoZWNrc3VtIjoiTExNU0NEL2tvd1ZvcTdzaUFrUzBuUT09Iiwic2VydmljZV9uYW1lIjoibG9jYWwifSwiZXhwIjoiMjAyNC0wOS0xMFQwMzozMTo1Ny4yOTZaIiwicHVyIjoiYmxvYl90b2tlbiJ9fQ==--2a45eef773eedda24d6c25a4dd05da55aaadaee8",
    //         "headers": {
    //             "Content-Type": "image/jpeg"
    //         }
    //     }
    // }
    const presignData = await presignResponse.json
    const { 
      direct_upload: {
        headers,
        url,
      },
    } = presignData

    // https://uppy.io/docs/aws-s3/#getuploadparametersfile-options
    return {
      ...presignData,
      headers,
      method: 'PUT',
      url,
    }
  }

  async uploadSuccess(file){
    this.setState({ files: [...this.state.files, file] })
  }

  removeFile(e) {
    const {
      target: {
        parentElement: {
          dataset: { fileId }
        },
      },
    } = e

    const filteredFiles = this.state.files.filter((file) => {
      const {
        meta: {
          signed_id,
        }
      } = file

      return signed_id !== fileId
    })

    this.deleteFile(fileId)

    this.setState({ files: filteredFiles })
  }

  async deleteFile(fileId) {
    if (!this.destroyBlobUrlValue) {
      return
    }

    await destroy(this.destroyBlobUrlValue, {
      contentType: 'application/json',
      body: JSON.stringify({
        signed_id: fileId,
      }),
    })
  }

  render() {
    const { files } = this.state

    if (!this.hasAssetFileContainerTarget || !this.hasAssetFileTemplateTarget) {
      return
    }

    // Remove all existing Asset Files
    Array.from(this.assetFileContainerTarget.getElementsByClassName('asset-file')).forEach((el) => {
      el.remove()
    })

    // Add Asset Files
    files.forEach(file => {
      const {
        meta: {
          name,
          signed_id,
        }
      } = file

      // Add Asset File
      const assetFileNode = this.assetFileTemplateTarget.cloneNode(true)
      assetFileNode.classList.add('asset-file')
      assetFileNode.classList.remove('hidden')
      assetFileNode.dataset.fileId = signed_id
      assetFileNode.id = `new_comment_asset_file_${signed_id}`
      assetFileNode.querySelector('#new_comment_asset_file_name').textContent = name

      this.assetFileContainerTarget.appendChild(assetFileNode)

      // Add Input
      const input = document.createElement('input')
      input.classList.add('asset-file')
      input.type = 'hidden'
      input.name = `comment[asset_files_attributes][][file]`
      input.value = signed_id

      this.assetFileContainerTarget.appendChild(input)
    })
  }
}